import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Badge, Button, Card, Input, Modal, ModalFooter, Select } from '@/components/common';
import { getErrorMessage } from '@/services';
import {
  MONTH_STATE_LABEL,
  insurerPaymentsService,
  type MonthState,
  type PaymentRow,
} from '@/services/insurerPayments';
import {
  SETTLEMENT_MODE_OPTIONS,
  SETTLEMENT_STATUS_LABEL,
  formatPeriod,
  insurerSettlementsService,
  type InsurerSettlement,
  type SettlementMode,
} from '@/services/insurerSettlements';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import { useAuthStore } from '@/store';
import { formatCurrency, formatDate } from '@/utils';

/**
 * #360 / #444: the insurer's payment tracking. One row per broker-month with
 * Calculated, Approved, Paid and Pending; a row opens the month where the
 * insurer admin approves it and records or voids the payments it made.
 * Nothing here touches the broker's books.
 */

const STATE_VARIANT: Record<MonthState, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  open: 'default',
  approved: 'info',
  part_paid: 'warning',
  paid: 'success',
  disputed: 'error',
};

const TH = 'px-3 py-2 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300';
const TD = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100';
const STATE_OPTIONS = [
  { value: '', label: 'All states' },
  ...(Object.keys(MONTH_STATE_LABEL) as MonthState[]).map((s) => ({ value: s, label: MONTH_STATE_LABEL[s] })),
];

export function PaymentTrackingTab() {
  const [state, setState] = useState<MonthState | ''>('');
  const [open, setOpen] = useState<PaymentRow | null>(null);
  const [downloading, setDownloading] = useState(false);

  const list = useQuery({
    queryKey: ['insurerPayments', state],
    queryFn: () => insurerPaymentsService.list({ state }),
  });

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await insurerPaymentsService.exportCsv({ state });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'commission_payments.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the payments. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const kpis = list.data?.kpis;
  const rows = list.data?.results ?? [];
  const tiles = kpis
    ? [
        { label: 'Calculated', value: kpis.calculated, help: 'Commission due under your published schedules.' },
        { label: 'Approved', value: kpis.approved, help: 'Months you have signed off for payment.' },
        { label: 'Paid', value: kpis.paid, help: 'Payments you have recorded (voided ones excluded).' },
        { label: 'Pending', value: kpis.pending, help: 'Approved minus paid.' },
      ]
    : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label} className="p-4">
            <div title={t.help}>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(t.value)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t.label}</p>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div className="w-full sm:w-48">
            <Select
              name="payment-state"
              label="State"
              options={STATE_OPTIONS}
              value={state}
              onChange={(e) => setState(e.target.value as MonthState | '')}
            />
          </div>
          <Button variant="outline" onClick={download} isLoading={downloading} disabled={downloading}>
            Export CSV
          </Button>
        </div>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Last 12 months. Calculated from your published schedules; payments are those you have recorded.
        </p>

        {list.isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading payments…</p>
        ) : list.isError ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            Could not load payments: {getErrorMessage(list.error)}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nothing to track yet. Publish a commission schedule so commission can be calculated.
          </p>
        ) : (
          <>
            <ul className="space-y-3 md:hidden" aria-label="Commission payments">
              {rows.map((r) => (
                <li key={`${r.broker_id}-${r.month}`} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.broker_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{formatPeriod(r.month)}</p>
                    </div>
                    <Badge variant={STATE_VARIANT[r.state]} size="sm">
                      {MONTH_STATE_LABEL[r.state]}
                    </Badge>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <dt>Calculated</dt>
                    <dd className="text-right">{formatCurrency(r.calculated)}</dd>
                    <dt>Approved</dt>
                    <dd className="text-right">{r.approved ? formatCurrency(r.approved) : '—'}</dd>
                    <dt>Paid</dt>
                    <dd className="text-right">{formatCurrency(r.paid)}</dd>
                    <dt>Pending</dt>
                    <dd className="text-right">{r.pending ? formatCurrency(r.pending) : '—'}</dd>
                  </dl>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => setOpen(r)}>
                    Open
                  </Button>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className={`${TH} text-left`}>Broker</th>
                    <th className={`${TH} text-left`}>Month</th>
                    <th className={`${TH} text-right`}>Calculated</th>
                    <th className={`${TH} text-right`}>Approved</th>
                    <th className={`${TH} text-right`}>Paid</th>
                    <th className={`${TH} text-right`}>Pending</th>
                    <th className={`${TH} text-left`}>State</th>
                    <th className={TH} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((r) => (
                    <tr key={`${r.broker_id}-${r.month}`}>
                      <td className={TD}>{r.broker_name}</td>
                      <td className={TD}>{formatPeriod(r.month)}</td>
                      <td className={`${TD} text-right`}>
                        {formatCurrency(r.calculated)}
                        {r.variance && Number(r.variance) !== 0 && (
                          <span className="ml-1 text-xs text-amber-700 dark:text-amber-400" title="Calculated has moved since you approved">
                            ({Number(r.variance) > 0 ? '+' : ''}
                            {formatCurrency(r.variance)})
                          </span>
                        )}
                      </td>
                      <td className={`${TD} text-right`}>{r.approved ? formatCurrency(r.approved) : '—'}</td>
                      <td className={`${TD} text-right`}>{formatCurrency(r.paid)}</td>
                      <td className={`${TD} text-right`}>
                        {r.pending ? formatCurrency(r.pending) : '—'}
                        {Number(r.on_account) > 0 && (
                          <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                            (on account {formatCurrency(r.on_account)})
                          </span>
                        )}
                      </td>
                      <td className={TD}>
                        <Badge variant={STATE_VARIANT[r.state]} size="sm">
                          {MONTH_STATE_LABEL[r.state]}
                        </Badge>
                        {r.unpriced_policies > 0 && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            {r.unpriced_policies} not in a schedule
                          </span>
                        )}
                      </td>
                      <td className={`${TD} text-right`}>
                        <Button size="sm" variant="outline" onClick={() => setOpen(r)}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {open && <MonthDetailModal row={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function MonthDetailModal({ row, onClose }: { row: PaymentRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { can } = useInsurerPermissions();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = can('commission_payments.edit');
  const [approveOpen, setApproveOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [voiding, setVoiding] = useState<InsurerSettlement | null>(null);

  const detail = useQuery({
    queryKey: ['insurerPaymentDetail', row.broker_id, row.month],
    queryFn: () => insurerPaymentsService.detail(row.broker_id, row.month),
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['insurerPaymentDetail'] });
    queryClient.invalidateQueries({ queryKey: ['insurerPayments'] });
  };
  const d = detail.data;

  return (
    <Modal isOpen onClose={onClose} size="full" title={`${row.broker_name} · ${formatPeriod(row.month)}`}>
      {detail.isLoading || !d ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {detail.isError ? `Could not load: ${getErrorMessage(detail.error)}` : 'Loading…'}
        </p>
      ) : (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Calculated', d.calculated],
              ['Approved', d.approved],
              ['Paid', d.paid],
              ['Pending', d.pending],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value ? formatCurrency(value) : '—'}</dd>
              </div>
            ))}
          </dl>
          {d.paid_before_approval && (
            <p className="rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              Payments were recorded before this month was approved.
            </p>
          )}
          {d.adjustments.length > 0 && (
            <ul className="text-xs text-gray-600 dark:text-gray-300">
              {d.adjustments.map((a) => (
                <li key={a.at}>
                  Adjusted from {formatCurrency(a.previous)} to {formatCurrency(a.approved)}: {a.reason}
                </li>
              ))}
            </ul>
          )}
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setApproveOpen(true)}>
                {d.approved ? 'Adjust approved amount' : 'Approve month'}
              </Button>
              <Button onClick={() => setRecordOpen(true)}>Record payment</Button>
            </div>
          )}

          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Payments</h4>
          {d.settlements.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No payments recorded for this month.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {d.settlements.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(s.amount)} · {formatDate(s.paid_on)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {s.reference} · {SETTLEMENT_STATUS_LABEL[s.status]}
                      {s.status === 'disputed' && s.disputed_amount && ` · broker says ${formatCurrency(s.disputed_amount)}`}
                      {s.status === 'voided' && s.void_reason && ` · ${s.void_reason}`}
                    </p>
                  </div>
                  {role === 'insurer_admin' && s.status !== 'voided' && (
                    <Button size="sm" variant="outline" onClick={() => setVoiding(s)}>
                      Void
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {approveOpen && d && (
        <ApproveDialog row={d} onClose={() => setApproveOpen(false)} onDone={refresh} />
      )}
      {recordOpen && d && (
        <RecordPaymentDialog row={d} onClose={() => setRecordOpen(false)} onDone={refresh} />
      )}
      {voiding && <VoidDialog settlement={voiding} onClose={() => setVoiding(null)} onDone={refresh} />}
    </Modal>
  );
}

function ApproveDialog({ row, onClose, onDone }: { row: PaymentRow; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState(row.approved ?? row.calculated);
  const [reason, setReason] = useState('');
  const differs = Number(amount) !== Number(row.approved ?? row.calculated);
  const needReason = row.approved ? true : differs;
  const mutation = useMutation({
    mutationFn: () => insurerPaymentsService.approve(row.broker_id, row.month, { amount, reason }),
    onSuccess: () => {
      toast.success(row.approved ? 'Approved amount adjusted' : 'Month approved');
      onDone();
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal isOpen onClose={onClose} title={row.approved ? 'Adjust approved amount' : 'Approve month'}>
      <div className="space-y-3">
        <Input label="Approved amount (₹)" name="approve_amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input
          label={needReason ? 'Reason (required)' : 'Reason (optional)'}
          name="approve_reason"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!amount || (needReason && !reason.trim())}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function RecordPaymentDialog({ row, onClose, onDone }: { row: PaymentRow; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState(row.pending && Number(row.pending) > 0 ? row.pending : '');
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [mode, setMode] = useState<SettlementMode>('bank_transfer');
  const [note, setNote] = useState('');
  const [confirmClosed, setConfirmClosed] = useState(false);
  const [closedWarning, setClosedWarning] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      insurerSettlementsService.record({
        broker: row.broker_id,
        period: row.month.slice(0, 7),
        amount,
        paid_on: paidOn,
        reference,
        mode,
        note,
        ...(confirmClosed ? { confirm_closed_month: true } : {}),
      }),
    onSuccess: () => {
      toast.success('Payment recorded');
      onDone();
      onClose();
    },
    onError: (e) => {
      const data = (e as { response?: { data?: { code?: string; error?: string } } })?.response?.data;
      if (data?.code === 'month_closed') {
        setClosedWarning(data.error ?? 'This month is already fully paid.');
        setConfirmClosed(true);
      } else {
        toast.error(getErrorMessage(e));
      }
    },
  });
  return (
    <Modal isOpen onClose={onClose} title="Record payment" description={`${row.broker_name} · ${formatPeriod(row.month)}`}>
      <div className="space-y-3">
        <Input label="Amount (₹)" name="rec_amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        <Input label="Paid on" name="rec_paid_on" type="date" value={paidOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setPaidOn(e.target.value)} required />
        <Input label="Reference (UTR / cheque no.)" name="rec_reference" value={reference} maxLength={64} onChange={(e) => setReference(e.target.value)} required />
        <Select
          name="rec_mode"
          label="Mode"
          options={SETTLEMENT_MODE_OPTIONS}
          value={mode}
          onChange={(e) => setMode(e.target.value as SettlementMode)}
        />
        <Input label="Note (optional)" name="rec_note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        {closedWarning && (
          <p role="alert" className="rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            {closedWarning} Press Record payment again to confirm.
          </p>
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!amount || !reference.trim() || !paidOn}>
          Record payment
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function VoidDialog({ settlement, onClose, onDone }: { settlement: InsurerSettlement; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => insurerSettlementsService.void(settlement.id, reason),
    onSuccess: () => {
      toast.success('Payment voided');
      onDone();
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Void this payment"
      description={`${formatCurrency(settlement.amount)} · ${settlement.reference}. The row is kept; enter a corrected payment afterwards.`}
    >
      <Input label="Reason (required)" name="void_reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!reason.trim()}>
          Void payment
        </Button>
      </ModalFooter>
    </Modal>
  );
}
